import {
  BUY_TYPE,
  ORDER_BEHAVIOR,
  ORDER_TYPE,
  PAY_TYPE,
  USER_LEVEL_KEY,
  type IEquity,
  type LoginInfo,
  type UserInfo,
} from '~/api/interface';
import { postCreateOrder } from '~/api/module/rights.ts';
import type { IRightsConfig } from '~/composables/rights.ts';

import { getOrderCheckPurchaseLimit } from '~/api/module/order';
import { USER_ADMIN } from '~/constants';
import { PageKey } from '~/constants/page';
import { PRODUCT_IMAGES } from '~/constants/product';
import { PaymentRedirectUrlKey } from '~/constants/token';
import { usePaymentEquity } from '~/features/paymentDesk';
import { clearModals } from '~/hooks/useModal/modals/empty';
import { isThenable } from '~/utils/helper';

export interface IState {
  userInfo?: UserInfo | null;
  accountList: LoginInfo[];
  overseas: IRightsConfig | null;
}
export interface IParams extends IState {
  rights: IEquity;
}
export interface ITitleClass {
  bg: string;
  name: string;
  number: string;
  feature: string;
}

export default class BaseRights {
  rights!: IEquity;

  state: IState = {
    userInfo: null,
    accountList: [],
    overseas: null,
  };

  titleClass: ITitleClass = {
    bg: 'absolute top-0 left-1/2 w-[170px] -translate-x-1/2',
    name: 'text-[24px] leading-[33px] font-bold semibold title-name text-white',
    number: 'text-[14px] inline-block text-white mt-[10px] title-number',
    feature:
      'absolute bottom-[37px] w-full text-[12px] text-left text-white leading-[20px] suit-text',
  };

  constructor(params: IParams) {
    // console.log('params', params);
    this.state.userInfo = params.userInfo;
    this.state.accountList = params.accountList || [];
    this.state.overseas = params.overseas || null;
    this.rights = params.rights;
  }

  getButton(options: {
    disabled?: boolean;
    text: string;
    cb?(): undefined | Promise<void> | (() => void);
  }) {
    return defineComponent(() => {
      const pending = ref(false);
      const handleClick = async () => {
        if (!options.cb) {
          return;
        }

        const resourceLocation = injectResourceLocation();

        trackEvent(TRACK_EVENT.CLICK_SUBSCRIBE_PURCHASE, {
          package: this.rights.rights_name,
          source: resourceLocation?.source,
        });

        const proc = options.cb();

        if (proc && isThenable(proc)) {
          // 是个 promise
          pending.value = true;
          await proc;
          pending.value = false;
        }
      };
      return () => {
        return (
          <div
            class={options?.disabled ? 'sub-btn sub-btn-disabled' : 'sub-btn'}
            onClick={handleClick}
          >
            {options.text}
          </div>
        );
      };
    });
  }

  /** 用户是否已登录 */
  get isUserLogin() {
    return !!this.state.userInfo;
  }

  /** 有管理的团队体验/入门版 */
  hasManagedRights1516() {
    return this.state.accountList.some(
      a =>
        [USER_LEVEL_KEY.TEAM_TASTE, USER_LEVEL_KEY.TEAM_LIGHT].includes(a.rights_type) &&
        a.permission >= USER_ADMIN.ORG_ADMIN_USER,
    );
  }

  isPersonalRights() {
    if (!this.state.userInfo) {
      return false;
    }
    return USER_LEVEL_KEY.PERSONAL_LITE === this.state.userInfo.rights_type;
  }

  /** 当前登录用户是否是当前显示的套餐 */
  isSameRights() {
    if (!this.state.userInfo) {
      return false;
    }
    return this.state.userInfo.rights_type === this.rights.rights_type;
  }

  /** 个人（体验/付费）账号（管理员/成员） */
  get isPersonalAccount() {
    if (!this.state.userInfo) {
      return false;
    }

    return [USER_LEVEL_KEY.TASTE, USER_LEVEL_KEY.PERSONAL, USER_LEVEL_KEY.PERSONAL_LITE].includes(
      this.state.userInfo.rights_type,
    );
  }

  /** 团队（团队体验/入门/标准/旗舰）账号（管理员/成员） */
  get isOrgAccount() {
    if (!this.state.userInfo) {
      return false;
    }

    return ![USER_LEVEL_KEY.TASTE, USER_LEVEL_KEY.PERSONAL, USER_LEVEL_KEY.PERSONAL_LITE].includes(
      this.state.userInfo.rights_type,
    );
  }

  /** 团队（团队体验/入门/标准/旗舰）成员账号 */
  get isOrgMember() {
    if (!this.state.userInfo) {
      return false;
    }

    return (
      ![USER_LEVEL_KEY.TASTE, USER_LEVEL_KEY.PERSONAL, USER_LEVEL_KEY.PERSONAL_LITE].includes(
        this.state.userInfo.rights_type,
      ) && this.state.userInfo.permission === USER_ADMIN.NORMAL
    );
  }

  get isRights2122() {
    const { userInfo } = this.state;
    if (!userInfo) {
      return false;
    }

    return [USER_LEVEL_KEY.ORG_STANDARD, USER_LEVEL_KEY.ORG_FLAGSHIP].includes(
      userInfo.rights_type,
    );
  }

  toLogin() {
    goLogin({ redirect: encodeURIComponent(encodeURIComponent(location.href)) });
  }

  toCreateRights15() {
    const router = useRouter();
    router.push({ name: PageKey.TEAM_TASTE_CREATE });
  }

  toCreateRights16() {
    clearModals();
    const router = useRouter();
    router.push({ name: PageKey.TEAM_STARTER_CREATE });
  }

  switchAccount() {
    const showAccountType = useAccountType();
    trackEvent(TRACK_EVENT.TOGGLE_ACCOUNT_TOP_CLICK);
    showAccountType.value = 'select';
  }

  async buy(options: {
    title: string;
    behavior: ORDER_BEHAVIOR;
    order_type: ORDER_TYPE;
    meta?: string;
  }) {
    const { userInfo, overseas } = this.state;
    if (!userInfo) {
      return false;
    }

    // 检查购买限制
    await getOrderCheckPurchaseLimit({
      order_type: options.order_type,
    });

    const rights = this.rights;
    const { $t } = useNuxtApp();

    if (isGlobalVersion()) {
      const time = options.meta || overseas?.cycle || 'annually';
      const detail = rights.rights_by_time.find(
        v => v.months === (time === 'monthly' ? 1 : 12) && v.buy_type === BUY_TYPE.SUBSCRIBE, // 海外目前只有订阅
      )!;

      if (!detail) {
        return message.error($t('chuang-jian-ding-dan-shi-bai'));
      }

      try {
        sessionSetItem(PaymentRedirectUrlKey, location.href);

        const res = await postCreateOrder({
          pay_type: PAY_TYPE.STRIPE,
          rights_id: detail.id,
          first_name: $t('yong-hu'),
          last_name: userInfo.account_name,
          email: userInfo.email_address || '',
          success_url: `${location.origin}/paymentResult`,
          cancel_url: `${location.origin}/subscribe`,
          product_url: PRODUCT_IMAGES(rights.rights_type),
        });

        if (res.data?.vendor_redirect_url) {
          location.href = res.data.vendor_redirect_url;
        }
      } catch (e) {
        console.warn('[Buy] postCreateOrder error', e);
      }
    } else {
      const isRenew = userInfo.rights_type === this.rights.rights_type;
      // 国内的逻辑
      usePaymentEquity({
        title: this.rights.rights_name,
        isRenew,
        rightsType: this.rights.rights_type,
        userInfo,
        behavior: options.behavior,
        order_type: options.order_type,
      });
    }

    return true;
  }
}
